import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { produce } from 'immer';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  defer,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  retry,
  skip,
  Subject,
  switchMap,
  throwError,
} from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { FormListVariantType } from '@blaze-runtime/engine-domain';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import {
  ContainerElementMetaUiFeatures,
  type ElementFormState,
  findIdColumnName,
  isSourceBound,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  RenderElementDataRowContext,
} from '@blaze-shared/elements-domain';
import { getBackground } from '@blaze-shared/elements-domain';
import {
  BaseSchema,
  createMultipleValuesSchemaWithIsDeletedColumn,
  DatasetRow,
  type ElementSchema,
} from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  deepClone,
  delayUntil,
  isDeepEqual,
  isNullish,
  resizeRxUtils,
  useEmitTracker,
} from '@blaze-shared/shared-util';

import { FormListRenderElementInstance } from '../../canvas-elements/models/render-element.models';
import { FormStateService, FormValidationService } from '../../services/forms/runtime/public-api';
import { CanvasElementBaseFormlikeComponent } from '../__base/base-formlike.component';

import {
  AddNewRowResult,
  DatasetRowWithIndex,
  FormListRowsService,
} from './form-list-rows.service';
import { getFirstRowInputs, isFormListWrapping, isSoftEqual } from './form-list.utils';

@UntilDestroy()
@Component({
  selector: 'be-form-list',
  templateUrl: './form-list.component.html',
  styleUrls: ['./form-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormListComponent
  extends CanvasElementBaseFormlikeComponent<FormListVariantType>
  implements OnChanges, OnInit, AfterViewInit, OnDestroy, FormListRenderElementInstance
{
  private readonly elementRef = inject(ElementRef);
  private readonly formStateService = inject(FormStateService);
  private readonly formValidation = inject(FormValidationService);
  private readonly formListRowsService = inject(FormListRowsService);

  @Input() value: DatasetRow[] | undefined;
  @Input() formState?: ElementFormState;

  @HostBinding('style.--b-el-internal-padding') internalPadding?: string;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly validateEmitTracker = useEmitTracker();
  private readonly dataValue = this.getDataInput('value');
  private readonly value$$ = new BehaviorSubject<DatasetRowWithIndex[]>([]);
  private readonly viewInitialized$$ = new BehaviorSubject<boolean>(false);
  private readonly isWrapping$$ = new Subject<void>();
  private readonly emptyRows: Record<string, DatasetRow> = {};

  isSourceBound?: boolean;
  value$ = this.value$$.asObservable();

  readonly elementKeyRoot = 'b-el';
  elementKey!: string;
  showNamesAsHeaders = true;
  numberEntries = true;
  addFirstRowOnLoad = true;
  isFetchingNewId = signal(false);
  isDetailsList = false;

  generalTitleColor?: string;
  generalAccentColor?: string;

  labelEnabled!: boolean;
  labelText!: string;
  addNewLabel!: string;
  noDataMessage!: string;
  headerStyles: Record<string, string> | null = null;
  wrapperStyles: Record<string, string | undefined> | null = null;
  rowIdColumn: string;
  showAddAction = signal(false);
  showDeleteAction!: boolean;
  isWrapping$: Observable<boolean>;
  maxRowsCount?: number | null = null;

  get schema(): ElementSchema | undefined {
    return this.getEventSchema('change');
  }

  get shouldForceExpand(): boolean {
    return this.viewportMode === 'mobile' || this.isDetailsList;
  }

  get hasValidationError(): boolean | undefined {
    return !!this.formState?.errors?.minRows || !!this.formState?.errors?.maxRows;
  }

  get errorLabel(): string | undefined {
    return this.formState?.messages?.minRows ?? this.formState?.messages?.maxRows;
  }

  get rowCountBelowLimit(): boolean {
    const value = this.value?.filter((row) => !row._isDeleted) ?? [];
    return isNullish(this.maxRowsCount) || !value.length ? true : value.length < this.maxRowsCount;
  }

  constructor() {
    super();

    this.dataValue.schema$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateSchemas();
    });

    const getRowInputs$ = defer(() => {
      if (!this.value || !this.props?.content.model?.length) {
        return of(false);
      }
      const inputs = getFirstRowInputs(
        this.elementRef.nativeElement,
        this.props.content.model.filter((c) => c.visible).length,
      );
      if (!inputs) {
        return throwError(() => 'no inputs');
      }
      return of(inputs);
    }).pipe(retry({ delay: 100, count: 20 }));

    this.isWrapping$ = this.isWrapping$$.pipe(
      delayUntil(() => this.viewInitialized$$.pipe(filter(Boolean))),
      switchMap(() => getRowInputs$),
      catchError(() => of(false)),
      map((inputs) =>
        isFormListWrapping(inputs, this.slots?.slotNodesData['0'].alignment?.horizontal),
      ),
    );

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
        this.isDetailsList = this.elementType === 'formDetailsList';
      }

      const { content } = this.props ?? {};
      const { title } = content ?? {};

      this.labelEnabled = title?.enabled ?? false;

      const addFirstRowOnLoad = content?.options?.addFirstRowOnLoad ?? true;
      if (addFirstRowOnLoad !== this.addFirstRowOnLoad) {
        this.addFirstRowOnLoad = addFirstRowOnLoad;
        if (addFirstRowOnLoad && !this.value?.length) {
          this.addItem();
        } else if (!addFirstRowOnLoad && this.value?.length === 1 && this.value[0].__isEmptyish) {
          this.deleteRow(null, { row: this.value[0], dataIdx: 0 });
        }
      }
      this.showNamesAsHeaders = content?.options?.showFieldNameAsHeader ?? true;
      this.numberEntries = content?.options?.numberEntries ?? true;
      this.showAddAction.set(!this.isDetailsList && (content?.options?.showAddAction ?? true));
      this.noDataMessage = content?.options?.noDataMessage ?? '';
      this.showDeleteAction = !this.isDetailsList && (content?.options?.showDeleteAction ?? true);
      this.addNewLabel = content?.options?.addActionLabel ?? 'ADD NEW';
      const currentMaxRowsCount = this.maxRowsCount;
      this.maxRowsCount =
        content?.options?.hasMaxRows && content.options.maxRows
          ? +content.options.maxRows
          : undefined;
      if (currentMaxRowsCount !== this.maxRowsCount && currentMaxRowsCount !== null) {
        this.validateValue(undefined, true);
      }

      this.isSourceBound = isSourceBound(this.props?.data ?? [], 'value');

      if (!this.isSourceBound) {
        this.setDefaultEventSchema('change');
      }
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<ContainerElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe(({ title }) => {
        this.labelText = title ?? '';
        safeDetectChanges(this.cdr);
      });

    this.stylesCleanup.register(this.elementKey);

    this.dataValue.dataInput$
      .pipe(
        switchMap((payload) => {
          const { data, schema }: { data?: DatasetRow[]; schema?: BaseSchema } = payload || {};
          this.value = this.formListRowsService.attachEmptyishRows(this.elementId, data);
          if (!schema) {
            return of(false);
          }

          this.rowIdColumn = findIdColumnName(schema);
          if (!this.isDetailsList && !this.value.length) {
            return this.addFirstRowOnLoad && this.showAddAction()
              ? this.addNewRow()
              : of({ emptyValue: true });
          } else if (this.isDetailsList || this.value.length) {
            return of(true);
          }
          return of(false);
        }),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        this.handleDataChanges(result);
        this.isWrapping$$.next();
      });

    this.props$
      .pipe(
        map((props) => props?.content.model),
        filter(Boolean),
        distinctUntilChanged(isDeepEqual),
        untilDestroyed(this),
      )
      .subscribe(() => this.isWrapping$$.next());

    this.actions$
      .pipe(
        ofType(runtimeActions.clearRuntimeUiOutputs),
        filter(({ elementIds }) => !!elementIds.find((id) => id === this.elementId)),
        untilDestroyed(this),
      )
      .subscribe(() => {
        const value = this.getDataInputValue('value');
        if (value) {
          this.dataInput = { ...value, data: [] };
        }
      });

    merge(this.formListRowsService.emptyishRowsUpdated$, this.formListRowsService.valueRowsUpdated$)
      .pipe(
        filter(({ formListId }) => formListId === this.elementId),
        untilDestroyed(this),
      )
      .subscribe(({ dataRow }) => {
        this.value = this.value?.map((row) =>
          row[this.rowIdColumn] !== dataRow[this.rowIdColumn] ? row : dataRow,
        );
      });
  }

  ngAfterViewInit(): void {
    resizeRxUtils
      .fromResizeObserver(this.elementRef.nativeElement)
      .pipe(skip(1), debounceTime(150), untilDestroyed(this))
      .subscribe(() => this.isWrapping$$.next());
    this.viewInitialized$$.next(true);
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose(this.elementKey);
    this.formStateService.clear(this.elementId);
  }

  addItem(event?: MouseEvent): void {
    if (event) {
      markEventAsHandled(event);
    }
    this.addNewRow().subscribe((result) => {
      this.handleDataChanges(result);
    });
  }

  deleteRow(event: MouseEvent | null, item: DatasetRowWithIndex): void {
    if (event) {
      markEventAsHandled(event);
    }

    if (!this.rowIdColumn) {
      return;
    }

    const updated = produce(this.value ?? [], (draft) => {
      draft[item.dataIdx]._isDeleted = true;
      if (draft[item.dataIdx].__isEmptyish) {
        draft.splice(item.dataIdx, 1);
        this.formListRowsService.removeEmptyishRow(this.elementId, item.dataIdx);
      }
    });
    this.updateValue(updated);
    setTimeout(() => {
      this.updateOutput();
      this.validateValue();
    });
  }

  valueUpdated(rowContext: RenderElementDataRowContext, modelField?: string): void {
    this.outputEmitTracker.shouldEmit(this.value);
    if (!this.value || !this.rowIdColumn || this.isDetailsList) {
      return;
    }

    const rowIdColumn = this.rowIdColumn;
    const rowId = rowContext?.rowId;
    if (isNullish(rowId)) {
      return;
    }

    const rowIndex = this.value.findIndex((row) => row[rowIdColumn] === rowId);
    const dataRow = this.value[rowIndex];
    const emptyRow = this.emptyRows[rowId];
    if (
      emptyRow &&
      (modelField
        ? !isSoftEqual(emptyRow[modelField], dataRow[modelField])
        : !isDeepEqual(emptyRow, dataRow))
    ) {
      delete this.emptyRows[rowId];
      this.value = produce(this.value, (draft) => {
        delete draft[rowIndex].__isEmptyish;
      });
    }
  }

  validateValue(rowContext?: RenderElementDataRowContext, forceValidation?: boolean): void {
    if (!this.value || !this.rowIdColumn || this.isDetailsList) {
      return;
    }
    const rowIdColumn = this.rowIdColumn;
    const rowId = rowContext?.rowId;

    if (!isNullish(rowId)) {
      const rowIndex = this.value.findIndex((row) => row[rowIdColumn] === rowId);
      const dataRow = this.value[rowIndex];
      const displayIndex = this.value$$.value.findIndex((r) => r.row[rowIdColumn] === rowId);
      this.formValidation.validateFormListRow(
        this.elementId,
        this.schema,
        dataRow,
        rowIdColumn,
        displayIndex,
        this.value,
        this.props,
      );
      return;
    }

    if (this.validateEmitTracker.shouldEmit(this.value) || forceValidation) {
      this.formValidation.validateFormListRows(
        this.elementId,
        this.schema,
        this.value,
        rowIdColumn,
        this.props,
      );
    }
  }

  shouldEmitOutput(): boolean {
    return this.outputEmitTracker.hasChanges();
  }

  getInstanceValue(): DatasetRow[] {
    return this.formListRowsService.pruneEmptyishRows(this.elementId, this.value);
  }

  private addNewRow(): Observable<AddNewRowResult> {
    return this.formListRowsService.addNewRow(
      this.elementId,
      this.isFetchingNewId,
      this.props?.data,
      this.rowIdColumn,
      this.value?.length,
      true,
    );
  }

  private handleDataChanges(result: AddNewRowResult): void {
    if (typeof result === 'boolean') {
      if (result) {
        this.updateValue(this.value);
        this.updateOutput();
        this.validateValue();
        this.isWrapping$$.next();
      }
      return;
    }
    if ('emptyValue' in result) {
      this.updateValue(this.value);
      this.updateOutput();
      this.validateValue();
      return;
    }
    this.emptyRows[result.rowToValidate.rowId] = deepClone(result.newRow);
    const value = [...(this.value ?? []), result.newRow];
    this.updateValue(value);
    this.updateOutput();
    if (result.shouldValidateFull) {
      this.validateValue();
    }
    this.validateValue(result.rowToValidate);
  }

  private updateValue(value?: any[] | null): void {
    this.value = value || [];
    const displayed = this.value
      .map((row, dataIdx) => ({ row, dataIdx }))
      .filter((r) => !r.row._isDeleted);
    this.value$$.next(displayed);
  }

  private updateSchemas(): void {
    if (!this.dataValue.schema) {
      return;
    }

    const schema = this.createElementSchema('change', undefined, this.dataValue.schema);
    const eventSchema = createMultipleValuesSchemaWithIsDeletedColumn(schema);
    this.setDefaultEventSchema('change', eventSchema);
  }

  private updateOutput(): void {
    const isInitialData = !this.outputEmitTracker.hasPrevious();
    const schema = this.schema;
    const data = this.getInstanceValue();

    if (!schema || !this.outputEmitTracker.shouldEmit(data)) {
      return;
    }

    this.output.emit({
      eventType: 'change',
      data,
      isInitialData,
      schema,
    });
  }

  private updateStyles(): void {
    const { style } = this.props ?? {};
    const { general, titleBlock } = style || {};
    if (!general || !titleBlock) {
      return;
    }

    this.generalTitleColor = general.title.color1;
    this.generalAccentColor = general.accent.color1;
    this.internalPadding = general.offset.padding;

    const wrapperStyles = !this.isDetailsList
      ? makeContainerVisualEffectStyles(general.visualEffect)
      : {
          background: getBackground(general.fill),
          ...makeBorderStyles(general.outline, general.outlineWidth),
          borderRadius: general.radius !== '0px' ? general.radius : undefined,
          ...makeContainerVisualEffectStyles(general.visualEffect),
        };

    this.wrapperStyles = wrapperStyles;
    this.headerStyles = {
      paddingLeft:
        this.numberEntries && !this.isDetailsList
          ? '36px'
          : this.isUsingOldPaddingStrategy
            ? '16px'
            : '0',
    };
  }
}
